import React, { useCallback, useRef, useEffect, useState } from 'react';
import TimerSetup from '../components/Timer/TimerSetup';
import TimerActive from '../components/Timer/TimerActive';
import SessionCelebration from '../components/Timer/SessionCelebration';
import BreakTimer from '../components/Timer/BreakTimer';
import UpgradePrompt from '../components/Subscription/UpgradePrompt';
import ConfirmModal from '../components/UI/ConfirmModal';
import WiltFirstRunModal from '../components/UI/WiltFirstRunModal';
import { useTimer } from '../hooks/useTimer';
import { useVisibility } from '../hooks/useVisibility';
import { useStreak } from '../hooks/useStreak';
import { useSessions } from '../hooks/useSessions';
import { useSubscription } from '../hooks/useSubscription';
import { useAppStore } from '../store/useAppStore';
import { getXP, setXP } from '../utils/db';

const NORMAL_GRACE_MS = 30000;
const STRICT_GRACE_MS = 15000;

/**
 * Timer page — orchestrates the focus session flow:
 * Setup → Active (growing) → Celebration (completed) or Death (killed)
 *
 * Also manages streak updates and XP accumulation.
 */
export default function TimerPage({ onStateChange }) {
  const timer = useTimer();
  const { streak, recordCompletion } = useStreak();
  const { stats, refresh } = useSessions();
  const { getLimit, isPremium } = useSubscription();
  const sessionMetaRef = useRef({ tag: 'work', plantType: 'roma' });
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showGiveUpConfirm, setShowGiveUpConfirm] = useState(false);
  const strictMode = useAppStore((s) => s.strictMode);
  const wiltWarningSeen = useAppStore((s) => s.wiltWarningSeen);
  const markWiltWarningSeen = useAppStore((s) => s.markWiltWarningSeen);

  const dailyLimit = getLimit('dailySessions');
  const sessionsRemaining = Math.max(0, dailyLimit - stats.todaySessions);

  // Report state changes to parent (for nav visibility)
  useEffect(() => {
    if (onStateChange) onStateChange(timer.state);
  }, [timer.state, onStateChange]);

  // Wilt plant when grace period expires
  const handleGracePeriodExpired = useCallback(() => {
    timer.wiltPlant();
  }, [timer]);

  const { isWilting } = useVisibility({
    gracePeriodMs: strictMode ? STRICT_GRACE_MS : NORMAL_GRACE_MS,
    onGracePeriodExpired: handleGracePeriodExpired,
    enabled: timer.state === 'running',
  });

  // Start session
  const handleStart = useCallback(
    (minutes, tag, plantType) => {
      // Check daily session limit
      if (stats.todaySessions >= dailyLimit) {
        setShowUpgrade(true);
        return;
      }
      sessionMetaRef.current = { tag, plantType };
      timer.startSession(minutes, tag, plantType);
    },
    [timer, stats.todaySessions, dailyLimit]
  );

  const showFirstRunModal = !wiltWarningSeen && timer.state === 'idle';

  // Handle session completion — update streak and XP, then start break
  const handleContinue = useCallback(async () => {
    if (timer.sessionData?.completed) {
      // Update streak
      await recordCompletion();

      // Update XP
      const currentXP = await getXP();
      const newXP = {
        total: currentXP.total + (timer.sessionData.xpEarned || 0),
        level: currentXP.level,
      };
      await setXP(newXP);
    }

    // Start a break period instead of going straight to idle
    timer.startBreak();
    refresh();
  }, [timer, recordCompletion, refresh]);

  // Handle give up — open confirmation modal (no native window.confirm per UX guide)
  const handleGiveUp = useCallback(() => {
    setShowGiveUpConfirm(true);
  }, []);

  const confirmGiveUp = useCallback(() => {
    setShowGiveUpConfirm(false);
    timer.giveUp();
  }, [timer]);

  // Handle death acknowledgment
  const handleDeathContinue = useCallback(() => {
    timer.reset();
    refresh();
  }, [timer, refresh]);

  // Handle skip break
  const handleSkipBreak = useCallback(() => {
    timer.skipBreak();
    refresh();
  }, [timer, refresh]);

  // Render based on timer state
  switch (timer.state) {
    case 'running':
      return (
        <>
          <TimerActive
            remaining={timer.remaining}
            durationMs={timer.durationMs}
            progress={timer.progress}
            plantType={sessionMetaRef.current.plantType}
            tag={sessionMetaRef.current.tag}
            isWilting={isWilting}
            onGiveUp={handleGiveUp}
          />
          <ConfirmModal
            isOpen={showGiveUpConfirm}
            title="Give up this session?"
            message="Are you sure? Your plant will wilt."
            confirmLabel="Give up"
            cancelLabel="Keep focusing"
            variant="danger"
            onConfirm={confirmGiveUp}
            onCancel={() => setShowGiveUpConfirm(false)}
          />
        </>
      );

    case 'break':
      return (
        <BreakTimer
          remaining={timer.breakRemaining}
          durationMs={timer.breakDurationMs}
          isLongBreak={timer.isLongBreak}
          onSkip={handleSkipBreak}
        />
      );

    case 'completed':
      return (
        <SessionCelebration
          session={timer.sessionData}
          streak={streak}
          totalXP={stats.totalXP + (timer.sessionData?.xpEarned || 0)}
          onContinue={handleContinue}
          showUpgradeHint={!isPremium}
        />
      );

    case 'killed': {
      const gaveUp = timer.sessionData?.reason === 'giveup';
      return (
        <div className="flex flex-col items-center justify-center h-full gap-6 p-8 text-center">
          <svg viewBox="0 0 100 100" width="160" height="160" className="saturate-0 brightness-50">
            <line x1="50" y1="90" x2="50" y2="40" stroke="#5c3a1e" strokeWidth="4" strokeLinecap="round" transform="rotate(10 50 65)" />
            <ellipse cx="38" cy="50" rx="8" ry="4" fill="#8B7355" transform="rotate(-30 38 50)" opacity="0.5" />
            <ellipse cx="62" cy="55" rx="8" ry="4" fill="#8B7355" transform="rotate(40 62 55)" opacity="0.4" />
            <ellipse cx="50" cy="90" rx="24" ry="5" fill="#3d2010" opacity="0.3" />
          </svg>
          <h2 className="text-xl font-semibold text-tomato-600 dark:text-tomato-400">
            {gaveUp ? 'Session ended early' : 'Your plant has wilted'}
          </h2>
          <p className="text-sm text-[color:var(--color-text-secondary)]">
            {gaveUp
              ? 'You gave up — your plant didn’t make it. Try a shorter session next time.'
              : 'You left the page for too long. Stay focused next time!'}
          </p>
          <button
            type="button"
            onClick={handleDeathContinue}
            className="px-8 py-3 rounded-xl bg-[color:var(--color-bg-card)] text-[color:var(--color-text-primary)] font-semibold border border-[color:var(--glass-border)] hover:bg-[color:var(--color-bg-elevated)] transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    default:
      return (
        <>
          <TimerSetup
            onStart={handleStart}
            sessionsToday={stats.todaySessions}
            dailyLimit={dailyLimit}
            sessionsRemaining={sessionsRemaining}
            isPremium={isPremium}
          />
          {showUpgrade && (
            <UpgradePrompt
              reason={`You've used your ${dailyLimit} free sessions today. Upgrade for unlimited sessions.`}
              onDismiss={() => setShowUpgrade(false)}
            />
          )}
          <WiltFirstRunModal
            isOpen={showFirstRunModal}
            strictMode={strictMode}
            onAcknowledge={markWiltWarningSeen}
          />
        </>
      );
  }
}
